import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import COUNTRIES from "@/lib/countries";

// Form submissions POST to /api/submit on the FastAPI backend.
// The endpoint reuses the previous submit.php contract (same field names,
// same honeypot + time-trap + rate limit + Brevo delivery).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SUBMIT_ENDPOINT = `${BACKEND_URL}/api/submit`;

const VOLUMES = [
  "< 100 shipments / month",
  "100 – 1,000 / month",
  "1,000 – 10,000 / month",
  "10,000+ / month",
  "Pilot only",
];

const USE_CASES = [
  "High-value shipments",
  "Logistics pilots",
  "Temporary tracking",
  "Package visibility",
  "Asset monitoring",
  "Other",
];

export default function LeadForm({
  source = "speak-to-an-expert",
  compact = false,
  heading,
  subheading,
}) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    role: "",
    phone: "",
    country: "",
    shipment_volume: "",
    use_case: "",
    message: "",
    consent: false,
    // Honeypot field — must stay empty. Bots auto-fill anything that looks
    // like an input.
    website: "",
  });
  // Time-trap baseline. Backend rejects submissions completed in <2s.
  const renderedAtRef = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please complete name, company and email.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        source,
        rendered_at: renderedAtRef.current,
      };
      const res = await axios.post(SUBMIT_ENDPOINT, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.data?.ok) {
        setSuccess(true);
        toast.success("Thanks — a specialist will be in touch shortly.");
      } else {
        toast.error(res.data?.error || "Could not submit your enquiry.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="lead-form-success"
        className="border border-line bg-white p-8 lg:p-10"
      >
        <div className="h-10 w-10 grid place-items-center bg-brand-subtle text-brand mb-5">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-ink">
          Request received
        </h3>
        <p className="mt-3 text-ink-muted max-w-md">
          A specialist will reply within one business day with next steps.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lead-form"
      className="border border-line bg-white p-6 sm:p-8 lg:p-10"
    >
      {heading && (
        <div className="mb-6">
          <h3 className="font-heading text-2xl font-semibold text-ink">
            {heading}
          </h3>
          {subheading && (
            <p className="mt-2 text-sm text-ink-muted">{subheading}</p>
          )}
        </div>
      )}

      {/* Honeypot — hidden from humans, visible to bots. Submissions
          with this field filled are silently dropped by /api/submit. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label htmlFor="website">
          Website (leave blank)
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
          />
        </label>
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <Field label="Full name" required>
          <input
            type="text"
            value={form.name}
            onChange={set("name")}
            className="input-field"
            placeholder="Jane Doe"
            required
            data-testid="lead-input-name"
          />
        </Field>
        <Field label="Company" required>
          <input
            type="text"
            value={form.company}
            onChange={set("company")}
            className="input-field"
            placeholder="Acme Logistics"
            required
            data-testid="lead-input-company"
          />
        </Field>
        <Field label="Work email" required>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            className="input-field"
            placeholder="jane@company.com"
            required
            data-testid="lead-input-email"
          />
        </Field>
        <Field label="Role">
          <input
            type="text"
            value={form.role}
            onChange={set("role")}
            className="input-field"
            placeholder="Head of Supply Chain"
            data-testid="lead-input-role"
          />
        </Field>
        <Field label="Country">
          <select
            value={form.country}
            onChange={set("country")}
            className="input-field"
            data-testid="lead-input-country"
          >
            <option value="">Select country</option>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Shipment volume">
          <select
            value={form.shipment_volume}
            onChange={set("shipment_volume")}
            className="input-field"
            data-testid="lead-input-volume"
          >
            <option value="">Select volume</option>
            {VOLUMES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Primary use case">
          <select
            value={form.use_case}
            onChange={set("use_case")}
            className="input-field"
            data-testid="lead-input-use-case"
          >
            <option value="">Select a use case</option>
            {USE_CASES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <Field label="What are you trying to track?">
          <textarea
            value={form.message}
            onChange={set("message")}
            className="input-field min-h-[110px] resize-y"
            placeholder="Tell us about lanes, shipment types, sensitivity, and timelines."
            data-testid="lead-input-message"
          />
        </Field>
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-ink-muted cursor-pointer">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={set("consent")}
          className="mt-0.5 h-4 w-4 accent-brand"
          data-testid="lead-input-consent"
        />
        <span>
          I agree to be contacted about disposable smart label solutions. We
          respect your inbox — see our privacy policy.
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary mt-6 w-full sm:w-auto"
        data-testid="lead-submit-button"
      >
        {submitting ? "Submitting…" : "Speak to an expert"}
        <ArrowUpRight className="h-4 w-4" />
      </button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
        {label} {required && <span className="text-brand">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
